/* Cowalkies "alive" layer — behaviour for assets/cw-motion.css.
   1. Image loading: any <img> still downloading gets a shimmer (.cw-img-loading) and fades in
      (.cw-img-in) when it lands. Images already loaded (cached) are left completely alone.
   2. Appear animations: cards / product-info blocks marked hidden by the CSS are revealed with a
      short stagger as they enter the viewport (.cw-shown + --cw-i).
   Both are picked up for content that arrives later too (catalog sort re-render, colour-swap
   gallery, page transitions) via a MutationObserver. */
(function () {
  if (window.__cwMotion) return;
  window.__cwMotion = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- 1. images ----------
  var SKIP_IMG = '.icon, [data-no-shimmer], header img';

  // Skip the fade for images that carry their own animation (e.g. the hero's slow drift) —
  // replacing it would restart or cancel that. Reading animationName forces a style recalculation,
  // and doing it inside each image's load handler (right after its class change dirtied styles)
  // meant one forced recalc per image as a page's photos landed. Queueing and reading them all in
  // a single frame — every read first, then every write — costs one recalc however many arrive.
  var fadeQueue = [];
  function queueFade(img) {
    fadeQueue.push(img);
    if (fadeQueue.length > 1) return;
    requestAnimationFrame(function () {
      var batch = fadeQueue;
      fadeQueue = [];
      var eligible = batch.filter(function (el) {
        return getComputedStyle(el).animationName === 'none';
      });
      eligible.forEach(function (el) {
        el.classList.add('cw-img-in');
        el.addEventListener(
          'animationend',
          function () {
            el.classList.remove('cw-img-in');
          },
          { once: true }
        );
      });
    });
  }

  function trackImage(img) {
    if (img.__cwTracked) return;
    img.__cwTracked = true;
    // data-no-fade photos (above the fold, LCP) show the CSS-only shimmer until they land but never
    // fade in; all this has to do is tell the CSS when the photo is there.
    if (img.hasAttribute('data-no-fade')) {
      var markLoaded = function () {
        img.setAttribute('data-cw-loaded', '');
      };
      if (img.complete) markLoaded();
      else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      }
      return;
    }
    if (img.matches(SKIP_IMG)) return;
    if (img.complete && img.naturalWidth > 0) return; // already there: nothing to wait for

    img.classList.add('cw-img-loading');

    var finished = false;
    function finish(fade) {
      if (finished) return;
      finished = true;
      img.classList.remove('cw-img-loading');
      if (fade && !reduce) queueFade(img);
    }
    img.addEventListener('load', function () {
      finish(true);
    });
    img.addEventListener('error', function () {
      finish(false);
    });
  }

  // ---------- 2. appear animations ----------
  var REVEAL_SELECTOR = '#product-grid .grid__item, [data-product-carousel] > *, .product__info-container > *';
  // Only the catalog grid animates content that arrives after first load (sorting re-renders it,
  // which is worth a soft entrance). The carousel and product-info column are re-rendered in place
  // by Dawn's variant/colour updates — those must appear instantly, not re-run the entrance.
  var DYNAMIC_SELECTOR = '#product-grid .grid__item';
  var supportsIO = 'IntersectionObserver' in window;
  var io = null;

  function reveal(el, index, instant) {
    el.style.setProperty('--cw-i', Math.min(index, 6));
    if (instant) el.classList.add('cw-instant');
    el.classList.add('cw-shown');
  }

  function observeReveal(el, dynamic) {
    if (el.classList.contains('cw-shown') || el.__cwObserved) return;
    el.__cwObserved = true;
    if (reduce || !supportsIO || (dynamic && !el.matches(DYNAMIC_SELECTOR))) {
      reveal(el, 0, true);
      return;
    }
    if (!io) {
      // Only the very first batch (what is on screen at page load) gets the staggered entrance.
      // Later batches come from scrolling: staggering rows that are already visible together made
      // them pop in one after another, like something looping over them.
      var firstBatch = true;
      io = new IntersectionObserver(
        function (entries) {
          var visible = entries
            .filter(function (e) {
              return e.isIntersecting;
            })
            .sort(function (a, b) {
              var pos = a.target.compareDocumentPosition(b.target);
              return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
            });
          var stagger = firstBatch;
          if (visible.length) firstBatch = false;
          visible.forEach(function (entry, i) {
            io.unobserve(entry.target);
            reveal(entry.target, stagger ? i : 0, false);
          });
        },
        // Trigger a little *before* a block reaches the screen (positive bottom margin, any
        // sliver of it). The old inset (-6%, needing 5% visible) left short rows — accordion
        // headers, the share button — on screen but still hidden at the bottom edge, so they
        // faded in after already being there.
        { rootMargin: '0px 0px 10% 0px', threshold: 0 }
      );
    }
    io.observe(el);
  }

  // ---------- wiring ----------
  function scan(root, dynamic) {
    if (root.nodeType !== 1) return;
    if (root.tagName === 'IMG') trackImage(root);
    root.querySelectorAll('img').forEach(trackImage);
    if (root.matches(REVEAL_SELECTOR)) observeReveal(root, dynamic);
    root.querySelectorAll(REVEAL_SELECTOR).forEach(function (el) {
      observeReveal(el, dynamic);
    });
  }

  function init() {
    scan(document.body, false);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          scan(node, true);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
